import { useCallback, useMemo } from "react";
import type { WorkerFilters } from "@/domains/workers/types";
import type { UserSalonRoleType } from "@/gql/graphql";
import type { TeamMember } from "../types";
import {
  useAssignUserRole,
  useCreateTeamMember,
  useDeleteTeamMember,
  useUpdateTeamMember,
} from "./use-team-member-mutation";
import { useTeamMemberQuery } from "./use-team-member-query";

interface TeamMemberFormData {
  displayName: string;
  bio?: string;
  canAcceptOnlineBookings?: boolean;
  isActive?: boolean;
  color: string;
  email?: string;
  salonRole?: "WORKER" | "MANAGER" | "OWNER";
}

interface UseTeamMembersProps {
  filters?: WorkerFilters;
}

export function useTeamMembers(props: UseTeamMembersProps = {}) {
  const { filters } = props;

  const { data, loading, error, refetch } = useTeamMemberQuery({ filters });
  const { execute: createTeamMember, isLoading: creating } = useCreateTeamMember();
  const { execute: updateTeamMember, isLoading: updating } = useUpdateTeamMember();
  const { execute: deleteTeamMember, isLoading: deleting } = useDeleteTeamMember();
  const { execute: assignUserRole, isLoading: assigningRole } = useAssignUserRole();

  // Transform workers data
  const teamMembers = useMemo(() => {
    if (!data?.teamMembers?.members) return [];
    return data.teamMembers.members;
  }, [data]);

  // Filter team members by status
  const activeTeamMembers = useMemo(() => {
    return teamMembers.filter((member) => member.isActive);
  }, [teamMembers]);

  const inactiveTeamMembers = useMemo(() => {
    return teamMembers.filter((member) => !member.isActive);
  }, [teamMembers]);

  const createMember = useCallback(
    async (input: TeamMemberFormData) => {
      return createTeamMember(input);
    },
    [createTeamMember]
  );

  const updateMember = useCallback(
    async (id: string, input: Partial<TeamMemberFormData>) => {
      return updateTeamMember({ id, ...input });
    },
    [updateTeamMember]
  );

  const deleteMember = useCallback(
    async (memberId: string) => {
      return deleteTeamMember(memberId);
    },
    [deleteTeamMember]
  );

  const toggleOnlineBooking = useCallback(
    async (member: TeamMember) => {
      return updateMember(member.id, {
        canAcceptOnlineBookings: !member.canAcceptOnlineBookings,
      });
    },
    [updateMember]
  );

  const toggleActiveStatus = useCallback(
    async (member: TeamMember) => {
      return updateMember(member.id, {
        isActive: !member.isActive,
      });
    },
    [updateMember]
  );

  const assignRole = useCallback(
    async (member: TeamMember, roles: UserSalonRoleType[]) => {
      if (!member.worker?.user?.id) {
        throw new Error("Member user ID not found");
      }
      return assignUserRole(member.worker.user.id, roles);
    },
    [assignUserRole]
  );

  return {
    // Data
    teamMembers,
    activeTeamMembers,
    inactiveTeamMembers,

    // State
    loading,
    error,
    creating,
    updating,
    deleting,
    assigningRole,

    // Actions
    createMember,
    updateMember,
    deleteMember,
    toggleOnlineBooking,
    toggleActiveStatus,
    assignRole,
    refetch,
  };
}
